/**
 * Correctif pour les problèmes d'importation Firebase et les erreurs de type
 * 
 * Ce fichier fournit des solutions pour les problèmes courants rencontrés avec Firebase
 * dans le projet CineDelux, notamment les erreurs d'importation et les problèmes de type.
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, doc, getDoc, setDoc, updateDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Configuration Firebase avec valeurs par défaut pour éviter les erreurs
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'placeholder-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'placeholder-project.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'placeholder-project',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'placeholder-project.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '000000000000',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:000000000000:web:0000000000000000000000',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-0000000000'
};

// Avertissement si les variables d'environnement sont manquantes
if (!import.meta.env.VITE_FIREBASE_API_KEY) {
  console.warn('⚠️ Variables d\'environnement Firebase manquantes. L\'application fonctionnera en mode dégradé.');
}

// Initialiser Firebase une seule fois
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Obtenir les services Firebase
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

/**
 * Fonction utilitaire pour convertir les Timestamps Firestore en dates
 * @param obj Objet contenant potentiellement des Timestamps
 * @returns Objet avec les Timestamps convertis en chaînes ISO
 */
export const convertTimestamps = (obj: any): any => {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  // Vérifier si c'est un Timestamp Firestore
  if (obj && typeof obj.toDate === 'function') {
    return obj.toDate().toISOString();
  }

  if (Array.isArray(obj)) {
    return obj.map(convertTimestamps);
  }

  const converted: any = {};
  for (const key in obj) {
    converted[key] = convertTimestamps(obj[key]);
  }
  return converted;
};

/**
 * Fonction utilitaire pour créer un document Firestore avec gestion d'erreur
 * @param collectionPath Chemin de la collection
 * @param docId ID du document
 * @param data Données à écrire
 */
export const createDocument = async (collectionPath: string, docId: string, data: any): Promise<void> => {
  try {
    const docRef = doc(db, collectionPath, docId);
    await setDoc(docRef, data);
    console.log(`Document créé avec succès: ${collectionPath}/${docId}`);
  } catch (error) {
    console.error(`Erreur lors de la création du document: ${collectionPath}/${docId}`, error);
    throw error;
  }
};

/**
 * Fonction utilitaire pour lire un document Firestore avec gestion d'erreur
 * @param collectionPath Chemin de la collection
 * @param docId ID du document
 * @returns Données du document ou null si non trouvé
 */
export const readDocument = async (collectionPath: string, docId: string): Promise<any | null> => {
  try {
    const docRef = doc(db, collectionPath, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return convertTimestamps(docSnap.data());
    } else {
      console.log(`Document non trouvé: ${collectionPath}/${docId}`);
      return null;
    }
  } catch (error) {
    console.error(`Erreur lors de la lecture du document: ${collectionPath}/${docId}`, error);
    throw error;
  }
};

/**
 * Fonction utilitaire pour mettre à jour un document Firestore avec gestion d'erreur
 * @param collectionPath Chemin de la collection
 * @param docId ID du document
 * @param data Données à mettre à jour
 */
export const updateDocument = async (collectionPath: string, docId: string, data: any): Promise<void> => {
  try {
    const docRef = doc(db, collectionPath, docId);
    await updateDoc(docRef, data);
    console.log(`Document mis à jour avec succès: ${collectionPath}/${docId}`);
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du document: ${collectionPath}/${docId}`, error);
    throw error;
  }
};

// Exporter toutes les fonctions et types Firestore nécessaires
export {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot
};

// Exporter le type Timestamp correctement
export type { Timestamp };

export default {
  auth,
  db,
  storage,
  convertTimestamps,
  createDocument,
  readDocument,
  updateDocument
};
